import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/empresa', label: 'Empresa' },
    { path: '/servicios', label: 'Servicios' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contacto', label: 'Contacto' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img
                src="https://public.readdy.ai/ai/img_res/a636c02b66d6d3f98ff640285b0b644c.jpg"
                alt="BIOTRAUMA Logo"
                className="h-16 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors relative group whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : isScrolled
                      ? 'text-dark hover:text-primary'
                      : 'text-white hover:text-primary'
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full ${
                      location.pathname === link.path ? 'w-full' : ''
                    }`}
                  />
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <a
                href="tel:+34629501941"
                className="px-6 py-2.5 bg-primary text-white font-medium rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-primary/50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Llamar Ahora
              </a>
              <a
                href="https://wa.me/34629501941?text=Hola,%20necesito%20información%20sobre%20sus%20servicios"
                target="_blank"
                rel="noopener noreferrer"
                className={`px-6 py-2.5 font-medium rounded-full border-2 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'border-primary text-primary hover:bg-primary hover:text-white'
                    : 'border-white text-white hover:bg-white hover:text-dark'
                }`}
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors cursor-pointer ${
                isScrolled ? 'text-dark' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-200"
          >
            <div className="px-6 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-dark hover:text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 space-y-3">
                <a
                  href="tel:+34629501941"
                  className="block w-full px-6 py-3 bg-primary text-white text-center font-medium rounded-full hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Llamar Ahora
                </a>
                <a
                  href="https://wa.me/34629501941?text=Hola,%20necesito%20información%20sobre%20sus%20servicios"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-6 py-3 border-2 border-primary text-primary text-center font-medium rounded-full hover:bg-primary hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* Mobile Sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white shadow-2xl border-t border-gray-200">
        <div className="grid grid-cols-2 gap-0">
          <a
            href="tel:+34629501941"
            className="flex items-center justify-center py-4 bg-primary text-white font-medium hover:bg-primary/90 transition-colors cursor-pointer"
          >
            <i className="ri-phone-line text-xl mr-2"></i>
            Llamar
          </a>
          <a
            href="https://wa.me/34629501941?text=Hola,%20necesito%20información%20sobre%20sus%20servicios"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center py-4 bg-white text-primary border-l border-gray-200 font-medium hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-whatsapp-line text-xl mr-2"></i>
            WhatsApp
          </a>
        </div>
      </div>

      {/* WhatsApp Floating Button - Desktop */}
      <a
        href="https://wa.me/34629501941?text=Hola,%20necesito%20información%20sobre%20sus%20servicios"
        target="_blank"
        rel="noopener noreferrer"
        className="hidden lg:flex fixed bottom-6 right-6 w-16 h-16 bg-primary text-white rounded-full items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300 z-50 animate-pulse-slow cursor-pointer"
        aria-label="Contactar por WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl"></i>
      </a>
    </>
  );
};

export default Navbar;
